import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Checkbox } from "../../../ui/checkbox";
import { Label } from "../../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Input } from "../../../ui/input";
import { Bell, CheckCircle2, Calendar, X } from "lucide-react";
import { toast } from "sonner";
import { DateTime } from "luxon";
import {
  getActiveReminderForProcess,
  createCheckpointReminder,
  cancelReminder,
} from "../../../../api/checkpointReminders";
import useRetryFetch from "../../../../hooks/useRetryFetch";

interface ReminderManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  processId: string;
  activityTitle?: string;
}

const ReminderManagementModal: React.FC<ReminderManagementModalProps> = ({
  isOpen,
  onClose,
  processId,
  activityTitle = "this activity",
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();

  // State
  const [activeReminder, setActiveReminder] = useState<any>(null);
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [setReminderEnabled, setSetReminderEnabled] = useState(false);
  const [reminderAction, setReminderAction] = useState<"resume_engagement" | "start_new_engagement">("resume_engagement");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch active reminder when modal opens
  useEffect(() => {
    if (isOpen && processId) {
      fetchActiveReminder();
      // Initialize selectedDate to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow);
      setSelectedTime("09:00");
    }
  }, [isOpen, processId]);

  const fetchActiveReminder = async () => {
    setLoadingReminder(true);
    try {
      const response = await getActiveReminderForProcess(retryFetch, processId);
      if (response.hasActiveReminder) {
        setActiveReminder(response.reminder);
        setSetReminderEnabled(false); // Hide create form if reminder exists
      } else {
        setActiveReminder(null);
      }
    } catch (error) {
      console.error("Failed to fetch reminder:", error);
    } finally {
      setLoadingReminder(false);
    }
  };

  const handleCancelReminder = async () => {
    if (!activeReminder) return;

    try {
      await cancelReminder(retryFetch, activeReminder._id);
      toast.success(t("reminder_cancelled_success"));
      setActiveReminder(null);
      setSetReminderEnabled(true); // Show create form after canceling
    } catch (error) {
      console.error("Failed to cancel reminder:", error);
      toast.error(t("reminder_cancel_failed"));
    }
  };

  const setQuickDate = (daysFromNow: number) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    setSelectedDate(date);
  };

  const handleCreateReminder = async () => {
    setIsSubmitting(true);
    try {
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);

      // Validate that scheduled time is in the future
      if (scheduledDateTime <= new Date()) {
        toast.warning(t("reminder_future_time_warning"));
        return;
      }

      await createCheckpointReminder(retryFetch, {
        processId,
        scheduledDateTime: scheduledDateTime.toISOString(),
        activityTitle,
        reminderAction,
        actionItems: [],
        insights: [],
      });

      const timeFromNow = DateTime.fromJSDate(scheduledDateTime).toRelative({ locale: i18n.language });
      toast.success(t("reminder_set_success", { timeFromNow }));

      // Refresh to show the new reminder
      await fetchActiveReminder();
      setSetReminderEnabled(false);
    } catch (error) {
      console.error("Failed to create reminder:", error);
      toast.error(t("reminder_set_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSetReminderEnabled(false);
    setActiveReminder(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
              <Bell className="w-6 h-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center">{t("reminder_manage_modal_title")}</DialogTitle>
          <DialogDescription className="text-center">
            {t("reminder_manage_modal_description")}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {loadingReminder ? (
            <p className="text-sm text-center text-muted-foreground">{t("loading")}</p>
          ) : activeReminder ? (
            // Show existing reminder
            <div className="p-4 space-y-3 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-medium">{t("reminder_active_label")}</span>
              </div>
              <div className="pl-6 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {DateTime.fromISO(activeReminder.scheduledDateTime).toLocaleString(
                      DateTime.DATETIME_MED
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Type:{" "}
                  {activeReminder.reminderAction === "resume_engagement"
                    ? t("reminder_type_resume")
                    : t("reminder_type_start_new")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {DateTime.fromISO(activeReminder.scheduledDateTime).toRelative({ locale: i18n.language })}
                </p>
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleCancelReminder}
                className="w-full mt-2"
              >
                <X className="w-4 h-4 mr-2" />
                {t("reminder_cancel_button")}
              </Button>
            </div>
          ) : (
            // No reminder - show option to set one
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="set-reminder"
                  checked={setReminderEnabled}
                  onCheckedChange={(checked) => setSetReminderEnabled(checked === true)}
                />
                <Label htmlFor="set-reminder" className="text-sm cursor-pointer">
                  {t("reminder_set_checkbox_label")}
                </Label>
              </div>

              {setReminderEnabled && (
                <div className="pl-6 space-y-3 animate-in slide-in-from-top-2">
                  {/* Reminder Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {t("reminder_action_prompt")}
                    </Label>
                    <RadioGroup
                      value={reminderAction}
                      onValueChange={(value: any) => setReminderAction(value)}
                      className="space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <RadioGroupItem value="resume_engagement" id="resume" />
                        <Label htmlFor="resume" className="leading-normal cursor-pointer">
                          <div className="text-sm font-medium">{t("reminder_type_resume")}</div>
                          <div className="text-xs text-muted-foreground">
                            {t("reminder_type_resume_desc")}
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-start gap-2">
                        <RadioGroupItem value="start_new_engagement" id="start-new" />
                        <Label htmlFor="start-new" className="leading-normal cursor-pointer">
                          <div className="text-sm font-medium">{t("reminder_type_start_new")}</div>
                          <div className="text-xs text-muted-foreground">
                            {t("reminder_type_start_new_desc")}
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Quick Date Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm">{t("reminder_when_prompt")}</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate(1)}
                        className="h-8 text-xs"
                      >
                        {t("reminder_quick_tomorrow")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate(2)}
                        className="h-8 text-xs"
                      >
                        {t("reminder_quick_2days")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate(7)}
                        className="h-8 text-xs"
                      >
                        {t("reminder_quick_1week")}
                      </Button>
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="reminder-time" className="text-sm">
                      {t("reminder_time_label")}
                    </Label>
                    <Input
                      id="reminder-time"
                      type="time"
                      dir="ltr"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  {/* Preview */}
                  <div className="text-xs text-muted-foreground">
                    {t("reminder_preview_label")}{" "}
                    {DateTime.fromJSDate(selectedDate)
                      .set({
                        hour: parseInt(selectedTime.split(":")[0]),
                        minute: parseInt(selectedTime.split(":")[1]),
                      })
                      .toRelative({ locale: i18n.language })}
                  </div>

                  <Button
                    onClick={handleCreateReminder}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? t("reminder_setting_button") : t("reminder_set_button")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3 text-sm rounded-lg bg-muted/50 text-muted-foreground">
          <p className="flex items-start gap-2">
            <Bell className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {t("reminder_info_message")}
            </span>
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={handleClose}>
            {t("close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderManagementModal;
